import { useQueryClient } from '@tanstack/react-query'
import { Form } from 'antd'
import { type FC } from 'react'
import { Controller } from 'react-hook-form'

import { BasicTd, BasicTh, BasicTr } from '@/components/elements'
import { LABELS } from '@/constants/common/message.constants'
import { getAdditionalPaymentCategoriesSelectQueryOptions } from '@/features/additional-payment-categories/query-options'
import { REPORT_TYPE } from '@/libs/api/types'

import {
  buildHistoryRestoreFieldValues,
  mapHistoryAdditionalToFormValueWithApcFallback
} from '../../models'
import { ShipmentOrderInputHistorySelect } from '../ShipmentOrderInputHistorySelect'

import { type ShipmentOrderCardNamePrefix } from './shipment-order-card-form-values'
import { useShipmentOrderCardField } from './use-shipment-order-card-field'

type Props = {
  namePrefix?: ShipmentOrderCardNamePrefix
  tenantKey: string
  onEditRequest: (req: unknown) => void
  /**
   * 履歴復元時に呼ばれる。呼び出し側 (`ShipmentOrderFormCard`) が
   * 「この荷主は履歴復元由来」を記録し、荷主 default の自動反映を抑止する
   * ために使う (`historyRestoredCustomerIdRef` 相当)。
   */
  onHistoryRestored: (customerId: number | null) => void
}

/**
 * create モード専用の履歴読込行。
 * 配送依頼履歴を選択すると、その内容をフォームへ per-field で復元する。
 * `reset()` は使わない（`shipments.${index}.` 接頭辞下で他フィールドを
 * 巻き込んで吹き飛ばすため）。
 */
export const HistoryLoadRow: FC<Props> = ({
  namePrefix,
  tenantKey,
  onEditRequest,
  onHistoryRestored
}) => {
  const queryClient = useQueryClient()
  const { name, watchField, setField, form } =
    useShipmentOrderCardField(namePrefix)
  const { control, trigger } = form

  const customerIdWatchValue = watchField('customerId') as number | null
  const shipmentCategoryIdWatchValue = watchField('shipmentCategoryId') as
    | number
    | null

  return (
    <BasicTr>
      <BasicTh>{LABELS.HISTORY}</BasicTh>
      <BasicTd colSpan={3}>
        <Controller
          name={name('historyId')}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Form.Item
              validateStatus={error?.message ? 'error' : undefined}
              help={error?.message}
              className="m-0"
            >
              <ShipmentOrderInputHistorySelect
                {...field}
                allowClear
                id={field.name}
                showSearch
                tenantKey={tenantKey}
                className="w-full"
                customerId={customerIdWatchValue ?? undefined}
                shipmentCategoryId={shipmentCategoryIdWatchValue ?? undefined}
                onHistorySelect={async (history) => {
                  // #4268: 履歴付帯行の税メタ null 補完に使う APC マスタ
                  // （Row のカテゴリ選択時 pre-fill と同一クエリキャッシュ）。
                  // 取得失敗時は補完なしで続行する（保存時の警告がセーフティネット）。
                  const additionalPaymentCategories = await queryClient
                    .ensureQueryData(
                      getAdditionalPaymentCategoriesSelectQueryOptions(
                        tenantKey,
                        REPORT_TYPE.DAILY
                      )
                    )
                    .catch(() => undefined)

                  // #3875 / #4076: この荷主は履歴の税区分/内税・付帯費用を優先し、
                  // 荷主 default の自動反映を抑止する。復元前に記録する。
                  onHistoryRestored(history.customerId ?? null)

                  const restoreValues = buildHistoryRestoreFieldValues(history)
                  Object.entries(restoreValues).forEach(([key, value]) => {
                    setField(key, value, {
                      shouldDirty: true,
                      shouldValidate: true
                    })
                  })

                  // #3875: 請求側付帯費用を履歴から復元する。#4268: 税メタが null の
                  // 履歴行は APC マスタの既定値で補完する。
                  setField(
                    'additionalCharges',
                    history.additionalCharges?.map(
                      mapHistoryAdditionalToFormValueWithApcFallback(
                        additionalPaymentCategories
                      )
                    ) ?? [],
                    { shouldDirty: true, shouldValidate: true }
                  )

                  // 復元後にバリデーションを再実行
                  setTimeout(() => {
                    trigger()
                  }, 0)
                }}
                onEditRequest={onEditRequest}
              />
            </Form.Item>
          )}
        />
      </BasicTd>
    </BasicTr>
  )
}
